import classNames from 'classnames';
import styles from './ModuleDefinition.module.css';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Dropdown,
  MenuProps,
  Space,
  Checkbox,
  CheckboxProps,
  List,
  Spin,
} from 'antd';
import React, { useState, useRef, useImperativeHandle, useEffect, forwardRef } from 'react';
import Modal from '../../Modal';
import { getSearchResultFromQueryParameter } from 'src/app/query.service';
import Icon from 'src/components/Icon/Icon';

interface FormData {
  [key: string]: any;
}

interface ModuleDefitnitionProps {
  isActive: boolean;
  onBack: () => void;
  propData: FormData;
  form: any;
  queryType: any;
  handleNext: (newData: { [key: string]: any }) => void;
}
interface SwPartNumber {
  id: string;
  softwarePartNumber: string;
}
interface HwPartNumber {
  id: string;
  hardwarePartNumber: string;
}
interface EcuCalibrationNumber {
  id: string;
  ecuCalibrationNumber: string;
}
interface EcuSerialNumber {
  id: string;
  ecuSerialNumber: string;
}

const ModuleDefitnition = forwardRef<any, ModuleDefitnitionProps>(
  ({ isActive, onBack, propData, form, queryType, handleNext }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isSmallText, setIsSmallText] = useState(false);
    const [status, setStatus] = useState<FormData[]>([]);
    const [swPartNumber, setSwPartNumber] = useState<FormData[]>([]);
    const [hwPartNumber, setHwPartNumber] = useState<FormData[]>([]);
    const [ecuCalibrationNumber, setEcuCalibrationNumber] = useState<FormData[]>([]);
    const [ecuSerialNumber, setEcuSerialNumber] = useState<FormData[]>([]);
    const [sourceCheck, setSourceCheck] = useState<string>('');
    const [tmpdata, setTmpData] = useState<{ [key: string]: any }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const inputRef = useRef(null);
    const [selectedValues, setSelectedValues] = useState<{ [key: string]: any }>({
      swPartNumber: [],
      hwPartNumber: [],
      ecuCalibrationNumber: [],
      ecuSerialNumber: [],
    });
    const onFinish = (values: any) => {};
    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValues({
          swPartNumber: [],
          hwPartNumber: [],
          ecuCalibrationNumber: [],
          ecuSerialNumber: [],
        });
      },
    }));
    useEffect(() => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        ...propData,
      }));
    }, [propData]);
    const applyMethod = (type: string) => {
      form.setFieldValue(type, selectedValues[type].join(', '));
      handleNext({ ...selectedValues });
      setOpenModal(false);
      setTmpData({});
      setSearchValue('');
    };
    const handleCancel = () => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [sourceCheck]: [...(tmpdata[sourceCheck] || [])],
      }));
      setOpenModal(false);
      setTmpData({});
      setSearchValue('');
    };
    const handleInputClick = async (placeholder: string) => {
      setInputPlaceholder(placeholder);
      setOpenModal(true);
      setIsLoading(true);
      setSearchValue('');
      form.setFieldValue('searchValues', '');
      switch (placeholder) {
        case 'SW Part Number':
          setSourceCheck('swPartNumber');
          setTmpData({ swPartNumber: [...selectedValues.swPartNumber] });
          try {
            const searchParameter = 'softwarePartNumber';
            const result: SwPartNumber[] = await getSearchResultFromQueryParameter<SwPartNumber>(
              queryType,
              searchParameter,
              selectedValues
            );
            setSwPartNumber(result);
          } catch (error) {
            console.log('error...', error);
            setIsLoading(false);
          }
          break;
        case 'HW Part Number':
          setSourceCheck('hwPartNumber');
          setTmpData({ hwPartNumber: [...selectedValues.hwPartNumber] });
          try {
            const searchParameter = 'hardwarePartNumber';
            const result: HwPartNumber[] = await getSearchResultFromQueryParameter<HwPartNumber>(
              queryType,
              searchParameter,
              selectedValues
            );
            setHwPartNumber(result);
          } catch (error) {
            setIsLoading(false);
          }
          break;
        case 'Ecu Calibration Number':
          setSourceCheck('ecuCalibrationNumber');
          setTmpData({ ecuCalibrationNumber: [...selectedValues.ecuCalibrationNumber] });
          try {
            const searchParameter = 'ecuCalibrationNumber';
            const result: EcuCalibrationNumber[] =
              await getSearchResultFromQueryParameter<EcuCalibrationNumber>(
                queryType,
                searchParameter,
                selectedValues
              );
            setEcuCalibrationNumber(result);
          } catch (error) {
            setIsLoading(false);
          }
          break;
        case 'Ecu Serial Number':
          setSourceCheck('ecuSerialNumber');
          setTmpData({ ecuSerialNumber: [...selectedValues.ecuSerialNumber] });
          try {
            const searchParameter = 'ecuSerialNumber';
            const result: EcuSerialNumber[] =
              await getSearchResultFromQueryParameter<EcuSerialNumber>(
                queryType,
                searchParameter,
                selectedValues
              );
            setEcuSerialNumber(result);
          } catch (error) {
            setIsLoading(false);
          }
          break;
      }
      setIsLoading(false);
    };

    const handleModalInputFocus = () => {
      setIsInputFocused(true);
      setIsSmallText(true);
    };

    const handleModalInputBlur = () => {
      setIsInputFocused(false);
      setIsSmallText(false);
    };

    const onLinkClick = (type: string, value: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.includes(value)
          ? prevValues[type]
          : [...(prevValues[type] || []), value],
      }));
    };
    const handleRemove = (type: string, itemRemove: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.filter((item: string) => item !== itemRemove),
      }));
    };

    const filteredSwPart = swPartNumber.filter((source) =>
      source.softwarePartNumber?.toLowerCase().includes(searchValue?.toLowerCase())
    );
    const filteredHwPart = hwPartNumber.filter((source) =>
      source.hardwarePartNumber?.toLowerCase().includes(searchValue?.toLowerCase())
    );
    const filteredEcuCalibration = ecuCalibrationNumber.filter((source) =>
      source.ecuCalibrationNumber?.toLowerCase().includes(searchValue?.toLowerCase())
    );
    const filteredEcuSerial = ecuSerialNumber.filter((source) =>
      source.ecuSerialNumber?.toLowerCase().includes(searchValue?.toLowerCase())
    );

    const handleInputClear = (type: string) => {
      form.setFieldValue(type, ``);
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: [],
      }));
    };

    const handlePlaceholderClick = () => {
      // Focus the input when the placeholder is clicked
      inputRef.current.focus();
    };

    const shouldShowResetButton = [
      'swPartNumber',
      'hwPartNumber',
      'ecuCalibrationNumber',
      'ecuSerialNumber',
    ].some((key) => {
      const value = selectedValues[key];
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    const handleReset = () => {
      form.setFieldValue('swPartNumber', '');
      form.setFieldValue('hwPartNumber', '');
      form.setFieldValue('ecuCalibrationNumber', '');
      form.setFieldValue('ecuSerialNumber', '');
      setSelectedValues({
        swPartNumber: [],
        hwPartNumber: [],
        ecuCalibrationNumber: [],
        ecuSerialNumber: [],
      });
    };

    const moreItems: MenuProps['items'] = [
      {
        key: '1',
        label: '5',
      },
      {
        key: '2',
        label: '10',
      },
      {
        key: '3',
        label: '15',
      },
      {
        key: '4',
        label: '25',
      },
      {
        key: '4',
        label: '50',
      },
      {
        key: '4',
        label: '70',
      },
    ];
    return (
      <div className={classNames(styles.ModuleDefitnition)}>
        <div
          className={`
            ${styles['ModuleDefitnition--QueryTitle']} 
            ${
              isActive ? styles['ModuleDefitnition--active'] : styles['ModuleDefitnition--InActive']
            }
            `}
        >
          Module Definition
          {shouldShowResetButton && (
            <div style={{ display: 'inline', float: 'right', marginRight: '30px' }}>
              <Button
                className={`${
                  isActive
                    ? styles['ModuleDefitnition--Reset']
                    : styles['ModuleDefitnition--InActive']
                }`}
                type="primary"
                htmlType="submit"
                onClick={handleReset}
                disabled={!isActive}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        <div className={classNames(styles['ModuleDefitnition--FormContainer'])}>
          <Form
            form={form}
            name="module_definition_data_form"
            onFinish={onFinish}
            layout="horizontal"
            disabled={!isActive}
          >
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  name="swPartNumber"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('SW Part Number')}
                    placeholder="SW Part Number"
                    suffix={
                      selectedValues.swPartNumber?.length > 0 && (
                        <span onClick={() => handleInputClear('swPartNumber')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.swPartNumber?.length > 0 && (
                  <span className={`${styles['ModuleDefitnition--Placeholder']} ${styles.small}`}>
                    SW Part Number ({selectedValues.swPartNumber?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="hwPartNumber"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('HW Part Number')}
                    placeholder="HW Part Number"
                    suffix={
                      selectedValues.hwPartNumber?.length > 0 && (
                        <span onClick={() => handleInputClear('hwPartNumber')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.hwPartNumber?.length > 0 && (
                  <span className={`${styles['ModuleDefitnition--Placeholder']} ${styles.small}`}>
                    HW Part Number ({selectedValues.hwPartNumber?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="ecuCalibrationNumber"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Ecu Calibration Number')}
                    placeholder="Ecu Calibration Number"
                    suffix={
                      selectedValues.ecuCalibrationNumber?.length > 0 && (
                        <span onClick={() => handleInputClear('ecuCalibrationNumber')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.ecuCalibrationNumber?.length > 0 && (
                  <span className={`${styles['ModuleDefitnition--Placeholder']} ${styles.small}`}>
                    Ecu Calibration Number ({selectedValues.ecuCalibrationNumber?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="ecuSerialNumber"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Ecu Serial Number')}
                    placeholder="Ecu Serial Number"
                    suffix={
                      selectedValues.ecuSerialNumber?.length > 0 && (
                        <span onClick={() => handleInputClear('ecuSerialNumber')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.ecuSerialNumber?.length > 0 && (
                  <span className={`${styles['ModuleDefitnition--Placeholder']} ${styles.small}`}>
                    Ecu Serial Number ({selectedValues.ecuSerialNumber?.length})
                  </span>
                )}
              </Col>
            </Row>

            {isActive && (
              <Row justify="start">
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['ModuleDefitnition--Buttons'])}
                      type="primary"
                      onClick={onBack}
                      disabled={!isActive}
                    >
                      Back
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Modal open={openModal} onClose={handleCancel} width={900} footer={null}>
              <div className={classNames(styles['ModuleDefitnition--ModalHeader'])}>
                Select {inputPlaceholder}
              </div>
              <div className={classNames(styles['ModuleDefitnition--ModelContent'])}>
                <div>
                  <p className={classNames(styles['ModuleDefitnition--TagTitle'])}>Selection</p>
                  <div className={classNames(styles['ModuleDefitnition--TagsContainer'])}>
                    {(selectedValues[sourceCheck] || []).map((item: any, index: any) => {
                      return (
                        <Button
                          type="primary"
                          key={`${item}${index}`}
                          onClick={() => handleRemove(sourceCheck, item)}
                          className={`styles['selected-button']`}
                        >
                          {item}
                          <Icon
                            name="close"
                            className={`${styles['ModuleDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                          />
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div className={styles['ModuleDefitnition--Search']}>
                  <Row gutter={16} justify="space-between">
                    <Col span={8}>
                      <div style={{ position: 'relative' }}>
                        <Input
                          name="filterValue"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onFocus={handleModalInputFocus}
                          onBlur={handleModalInputBlur}
                          ref={inputRef} // Use ref to access the input
                        />
                        <span
                          className={`${styles['ModuleDefitnition--ModalInputText']} ${
                            isSmallText ? styles.small : ''
                          }`}
                          onClick={handlePlaceholderClick} // Click handler to focus the input
                        >
                          Look for {inputPlaceholder}
                        </span>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                        <Button className={`styles['ModuleDefitnition--ModalDropDown']`}>
                          Limit
                          <Icon
                            name="arrow_drop_down"
                            className={`styles['ModuleDefitnition--ModalDropDownIcon']`}
                          />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </div>
                <div className={`styles['ModuleDefitnition--SearchList']`}>
                  {sourceCheck === 'swPartNumber' && (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={classNames(styles['loader-color'])} />,
                      }}
                      dataSource={filteredSwPart}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.swPartNumber.includes(item.softwarePartNumber)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('swPartNumber', item.softwarePartNumber)}
                        >
                          <span>{item.softwarePartNumber}</span>
                        </List.Item>
                      )}
                    />
                  )}
                  {sourceCheck === 'hwPartNumber' && (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={classNames(styles['loader-color'])} />,
                      }}
                      dataSource={filteredHwPart}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.hwPartNumber.includes(item.hardwarePartNumber)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('hwPartNumber', item.hardwarePartNumber)}
                        >
                          <span>{item.hardwarePartNumber}</span>
                        </List.Item>
                      )}
                    />
                  )}
                  {sourceCheck === 'ecuCalibrationNumber' && (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={classNames(styles['loader-color'])} />,
                      }}
                      dataSource={filteredEcuCalibration}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.ecuCalibrationNumber.includes(item.ecuCalibrationNumber)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() =>
                            onLinkClick('ecuCalibrationNumber', item.ecuCalibrationNumber)
                          }
                        >
                          <span>{item.ecuCalibrationNumber}</span>
                        </List.Item>
                      )}
                    />
                  )}
                  {sourceCheck === 'ecuSerialNumber' && (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={classNames(styles['loader-color'])} />,
                      }}
                      dataSource={filteredEcuSerial}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.ecuSerialNumber.includes(item.ecuSerialNumber)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('ecuSerialNumber', item.ecuSerialNumber)}
                        >
                          <span>{item.ecuSerialNumber}</span>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </div>
              <div className={`styles['ModuleDefitnition--ModalFooter']`}>
                <Space>
                  <Button
                    onClick={handleCancel}
                    className={`styles['ModuleDefitnition--CancelButton']`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => applyMethod(sourceCheck)}
                    className={`styles['ModuleDefitnition--Buttons']`}
                  >
                    Apply
                  </Button>
                </Space>
              </div>
            </Modal>
          </Form>
        </div>
      </div>
    );
  }
);
ModuleDefitnition.displayName = 'ModuleDefitnition';
export default ModuleDefitnition;
